import { useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useDispatch } from 'react-redux';

import { Grid } from '@material-ui/core';

import { Button, FormSelect } from 'components';

import { QB_RISK_ENDORSEMENTS, QB_RISK_QUOTES, useUpdatePostQuoteOptions } from 'lib/quoteBind';
import { hideModal } from 'stores';
import * as utils from 'utils';

import { useEditPostQuoteOptionsStyles } from './EditPostQuoteOptions.styles';

const EditPostQuoteOptions = ({ quote, postQuoteOptionGroupName, isEndorsement = false, riskId }) => {
  const classes = useEditPostQuoteOptionsStyles();
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  const postQuoteOptions = useMemo(() => utils.risk.getPostQuoteOptionsOptions(quote.postQuoteOptions), [quote.postQuoteOptions]);
  const activePostQuoteOptionsGroup = useMemo(
    () => postQuoteOptions[postQuoteOptionGroupName],
    [postQuoteOptions, postQuoteOptionGroupName]
  );
  const [selectedPostQuoteOptions, setSelectedPostQuoteOptions] = useState(() =>
    Object.fromEntries(
      activePostQuoteOptionsGroup.map((option) => [option.name, option.options.find((option) => option.selected) ?? option.options[0]])
    )
  );

  const { mutateAsync: updatePostQuoteOptions, isLoading: isUpdatingPostQuoteOptions } = useUpdatePostQuoteOptions();

  const handleSelect = (key, value) => {
    setSelectedPostQuoteOptions({ ...selectedPostQuoteOptions, [key]: value });
  };

  const handleSubmit = async () => {
    const newPostQuoteOptions = activePostQuoteOptionsGroup.map((postQuoteOption) => ({
      ...postQuoteOption,
      options: postQuoteOption.options.map((option) => ({
        ...option,
        selected: option.optionName === selectedPostQuoteOptions[postQuoteOption.name].optionName,
      })),
    }));

    await updatePostQuoteOptions({
      type: isEndorsement ? 'ENDORSEMENT' : 'QUOTE',
      postQuoteOptions: {
        ...quote.postQuoteOptions,
        [postQuoteOptionGroupName]: newPostQuoteOptions,
      },
      id: quote.id,
    });

    if (isEndorsement) {
      queryClient.invalidateQueries(QB_RISK_ENDORSEMENTS, riskId);
    } else {
      queryClient.invalidateQueries([QB_RISK_QUOTES, { id: riskId }]);
    }

    dispatch(hideModal());
  };

  return (
    <Grid container spacing={2} className={classes.root}>
      {activePostQuoteOptionsGroup.length > 0 && (
        <>
          {activePostQuoteOptionsGroup.map((postQuoteOption) => (
            <Grid item xs={12} md={4} key={postQuoteOption.name}>
              <FormSelect
                name={postQuoteOption.name}
                label={postQuoteOption.nameLabel ?? postQuoteOption.name}
                handleUpdate={handleSelect}
                value={selectedPostQuoteOptions[postQuoteOption.name]}
                options={postQuoteOption.options.map((option) => ({
                  label: option.optionName,
                  value: option,
                }))}
                rules={{ required: true }}
                disabled={isUpdatingPostQuoteOptions}
              />
            </Grid>
          ))}
          <Grid item xs={12} style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button text="Submit" onClick={handleSubmit} color="primary" disabled={isUpdatingPostQuoteOptions} />
          </Grid>
        </>
      )}
    </Grid>
  );
};

export default EditPostQuoteOptions;
